import { useEffect, useState, type ReactNode } from 'react'
import type React from 'react'

import { Button } from '@/common/ui/Form/Button'

import { areBrandNamesEqual, useApplySuggestedBrand } from '../../model/useApplySuggestedBrand'

const isBrandMismatch = (currentBrandName: string | null | undefined, suggestedBrand: string) =>
    !areBrandNamesEqual(currentBrandName, suggestedBrand)

type ProductSuggestedBrandPopupProps = {
    suggestedBrand: string | null | undefined
    generationId: string | null | undefined
    productId: string
    currentBrandName: string | null | undefined
    isAnalyticsCompleted: boolean
    children: ReactNode
}

export const ProductSuggestedBrandPopup = ({
    suggestedBrand,
    generationId,
    productId,
    currentBrandName,
    isAnalyticsCompleted,
    children,
}: ProductSuggestedBrandPopupProps) => {
    const trimmedSuggestedBrand = suggestedBrand?.trim() ?? ''
    const [showPopup, setShowPopup] = useState(false)
    const [isDismissedInSession, setIsDismissedInSession] = useState(false)
    const [isApplied, setIsApplied] = useState(false)
    const { apply, isPending, error } = useApplySuggestedBrand(trimmedSuggestedBrand)

    useEffect(() => {
        setIsApplied(false)
        setIsDismissedInSession(false)
    }, [generationId, trimmedSuggestedBrand])

    useEffect(() => {
        if (isApplied || isDismissedInSession) {
            setShowPopup(false)
            return
        }

        const shouldShow =
            isAnalyticsCompleted &&
            Boolean(trimmedSuggestedBrand) &&
            Boolean(generationId) &&
            Boolean(productId) &&
            isBrandMismatch(currentBrandName, trimmedSuggestedBrand)

        setShowPopup(shouldShow)
    }, [
        currentBrandName,
        generationId,
        isAnalyticsCompleted,
        isApplied,
        isDismissedInSession,
        productId,
        trimmedSuggestedBrand,
    ])

    const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setIsDismissedInSession(true)
        setShowPopup(false)
    }

    const handleApply = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()

        apply(() => {
            setIsApplied(true)
            setShowPopup(false)
        })
    }

    if (!showPopup) {
        return <>{children}</>
    }

    return (
        <div className='relative inline-flex'>
            {children}
            <div
                className='absolute md:left-0 right-0 md:right-auto top-[calc(100%+0.75rem)] z-50 w-72 rounded-2xl border border-white/10 bg-[#17121f] p-4 text-left shadow-2xl'
                role='status'
                aria-live='polite'
            >
                <button
                    type='button'
                    className='absolute cursor-pointer right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full text-[#aba7af] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#be96ff]'
                    aria-label='Закрыть'
                    onClick={handleClose}
                >
                    ×
                </button>
                <div className='pr-6'>
                    <p className='text-sm font-medium leading-5 text-white'>Установить бренд из аналитики?</p>
                    <p className='mt-2 mb-4 text-sm leading-5 text-[#aba7af]'>
                        Анализ определил бренд «{trimmedSuggestedBrand}». Установить его для этого товара?
                    </p>
                    {error && (
                        <p className='mb-3 text-sm text-error'>
                            {error instanceof Error ? error.message : 'Не удалось установить бренд'}
                        </p>
                    )}
                    <Button
                        type='button'
                        emphasis='accent'
                        onClick={handleApply}
                        size='md'
                        disabled={isPending}
                        loading={isPending}
                    >
                        Установить «{trimmedSuggestedBrand}»
                    </Button>
                </div>
            </div>
        </div>
    )
}
